import { useCallback, useEffect } from "react";
import log from "logbeacon";
import { pubsub } from "utils";
import { deviceDetector } from "@ospweb/sdk-ui";
import { ChatStore } from "../store";
import EdgenDrawerCallable from "../components/EdgenDrawerCallable";
import { useLoginModalProvider } from "./useLoginModalProvider";
import { useLoginProcess } from "./useLoginProcess";
import { useRedirectChat } from "./useRedirectChat";
import { useOpenChatModal } from "./useOpenChatModal";
import ReportTranslations from "@translate/hooks/useGlobalReportManager";
import { I18nUtil } from "@utils/i18n";
import {
  REPORT_ACCESS_EVENTS,
  reportAccessStore,
} from "@/src/stores/reportAccessStore";

export interface ReportAccessParams {
  type: "crypto" | "stock";
  symbol?: string;
  id?: string;
  isModal?: boolean;
}

const I18n = I18nUtil.createScoped(ReportTranslations);

/**
 * 全局报告访问管理器 Hook
 * 专门处理报告访问相关的全局逻辑
 *
 * 职责：
 * 1. 监听全局报告访问事件
 * 2. 处理登录拦截和继续流程
 * 3. 执行权限检查和聊天跳转
 *
 * 使用方式：
 * - 在页面级组件中调用 useGlobalReportManager()
 * - ReportRating 组件通过 reportAccessStore.requestReportAccess() 触发访问
 */

export const useGlobalReportManager = () => {
  const { handleCheckChatVaild } = useRedirectChat();
  const { open: openLoginModal } = useLoginModalProvider();
  const handleOpenChatModal = useOpenChatModal();

  // 核心业务逻辑：执行报告访问
  const executeReportAccess = useCallback(
    async (params: ReportAccessParams) => {
      try {
        const chatOptions = {
          cacheQuestion: `Give me a clear overview of ${params.symbol}. Should I invest in it?`,
          question: I18n.t(
            "Give me a clear overview of %{var0}. Should I invest in it?",
            {
              var0: params.symbol,
            },
          ),
          sign: `${params.type === "crypto" ? "token" : "stock"}_360_symbol#${params.symbol}+id#${params.id}`,
        };
        ChatStore.initChat = "";
        if (params?.isModal || deviceDetector.isMobile()) {
          handleOpenChatModal(chatOptions);
          return true;
        }
        await EdgenDrawerCallable.end(false);
        handleCheckChatVaild(chatOptions);
        return true;
      } catch (error) {
        log.error(
          "[useGlobalReportManager] Error executing report access",
          error,
        );
        return false;
      }
    },
    [handleCheckChatVaild, handleOpenChatModal],
  );

  // 登录成功后处理待处理的报告访问
  const handleLoginSuccessForReport = useCallback(async () => {
    const operation = reportAccessStore.getPendingOperation();
    if (operation) {
      await executeReportAccess(operation.params);
      reportAccessStore.clearPendingOperation();
    }
  }, [executeReportAccess]);

  // 使用通用的 useLoginProcess 处理登录成功流程
  // 保持与现有登录流程的完全兼容性
  useLoginProcess(handleLoginSuccessForReport, { waitForEquity: true });

  // 监听已登录用户的直接访问请求
  useEffect(() => {
    const handleDirectAccess = (params: ReportAccessParams) => {
      executeReportAccess(params);
    };
    const unsubscribe = pubsub.on(
      REPORT_ACCESS_EVENTS.REQUEST_ACCESS,
      handleDirectAccess,
    );
    return () => unsubscribe();
  }, [executeReportAccess]);

  // 监听未登录用户的访问请求（需要先登录）
  useEffect(() => {
    const handleLoginRequired = (_params: ReportAccessParams) => {
      // 待处理操作已由 reportAccessStore.requestReportAccess 设置
      openLoginModal?.();
    };
    const unsubscribe = pubsub.on(
      REPORT_ACCESS_EVENTS.LOGIN_REQUIRED,
      handleLoginRequired,
    );
    return () => unsubscribe();
  }, [openLoginModal]);

  // 这个 Hook 主要负责注册全局事件监听器，无需返回值
  return {};
};
